import React from 'react';
import { Box } from '@mui/material';
import { useInvoiceLocale } from '../../hooks/useInvoiceLocale';
import { getIntlLocale } from '../../utils/localeFormat';
import { indianAmountInWords } from '../../utils/indianAmountInWords';
import { formatPlaceOfSupply } from '../../utils/gstPlaceOfSupply';

interface PlatformBillingInvoicePrintProps {
  invoice: Record<string, any>;
  platform?: Record<string, any>;
  company?: Record<string, any> | null;
  plan?: Record<string, any> | null;
}

const styles = {
  root: {
    backgroundColor: '#fff',
    color: '#111',
    width: '100%',
    maxWidth: 794,
    margin: '0 auto',
    padding: 24,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: 12,
    lineHeight: 1.45,
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    tableLayout: 'fixed' as const,
  },
  cell: {
    border: '1px solid #333',
    padding: '6px 8px',
    fontSize: 12,
    verticalAlign: 'top' as const,
  },
  th: {
    border: '1px solid #333',
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: '#f5f5f5',
    verticalAlign: 'top' as const,
  },
  right: { textAlign: 'right' as const },
  bold: { fontWeight: 700 },
  title: { fontSize: 22, fontWeight: 700, letterSpacing: 0.5, margin: 0 },
  company: { fontSize: 18, fontWeight: 700, margin: '0 0 4px' },
};

function splitAddressLines(address?: string): string[] {
  if (!address) return [];
  return address.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

function buildTenantAddressLines(
  invoice: Record<string, any>,
  company?: Record<string, any> | null
): string[] {
  if (invoice.tenantAddress) {
    return splitAddressLines(invoice.tenantAddress);
  }
  const lines: string[] = [];
  if (company?.addressLine1) lines.push(company.addressLine1);
  const cityLine = [company?.city, company?.pincode].filter(Boolean).join(' ');
  if (cityLine) lines.push(cityLine);
  if (company?.state) lines.push(company.state);
  if (company?.country) lines.push(company.country);
  return lines;
}

export const PlatformBillingInvoicePrint: React.FC<PlatformBillingInvoicePrintProps> = ({
  invoice,
  platform,
  company,
  plan,
}) => {
  const { formatCurrency, formatDate, currency, language } = useInvoiceLocale(invoice);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat(getIntlLocale(language, currency), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount ?? 0);

  const platformName = platform?.companyName || 'Tripiigo';
  const platformAddressLines = splitAddressLines(platform?.address);
  const tenantGst = invoice.tenantGstNumber || company?.gstNumber || '';
  const taxable = invoice.taxableAmount ?? invoice.baseAmount ?? 0;
  const gstAmount = invoice.gstEnabled ? (invoice.gstAmount ?? 0) : 0;
  const gstRate = invoice.gstRate ?? platform?.defaultTaxRate ?? 18;
  const total = invoice.totalAmount ?? taxable + gstAmount;
  const balanceDue = invoice.status === 'paid' ? 0 : total;
  const paymentTerms = platform?.paymentTerms || 'Net 15';
  const itemName = invoice.itemDescription || invoice.planName || plan?.planName || 'Subscription';
  const hsnSac = invoice.hsnSac || plan?.hsnSac || platform?.hsnSac || '998314';
  const tenantAddressLines = buildTenantAddressLines(invoice, company);
  const placeOfSupply = formatPlaceOfSupply(tenantGst, company?.state);
  const igstLabel = `IGST${gstRate} (${gstRate}%)`;

  const bankNotes: string[] = [];
  bankNotes.push('Please use this account number :');
  if (platform?.bankName) bankNotes.push(platform.bankName);
  if (platform?.bankAccountName || platformName) {
    bankNotes.push(`Company Name ${platform?.bankAccountName || platformName}`);
  }
  if (platform?.bankAccount) bankNotes.push(`Account Number ${platform.bankAccount}`);
  if (platform?.bankIfsc) bankNotes.push(`IFSC Code ${platform.bankIfsc}`);
  if (platform?.bankAddress) bankNotes.push(`Bank Address ${platform.bankAddress}`);

  return (
    <Box id="platform-invoice-print" sx={{ '@media print': { p: 0 } }}>
      <div style={styles.root}>
        <table style={{ ...styles.table, marginBottom: 16 }}>
          <tbody>
            <tr>
              <td style={{ border: 'none', padding: 0, verticalAlign: 'top', width: '55%' }}>
                {platform?.logo && (
                  <img src={platform.logo} alt={platformName} style={{ maxHeight: 48, maxWidth: 160, marginBottom: 8, objectFit: 'contain' }} />
                )}
                <p style={styles.company}>{platformName}</p>
                {platformAddressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                {platform?.gstNumber && <div style={{ marginTop: 4 }}>GSTIN : {platform.gstNumber}</div>}
                {platform?.panNumber && <div>PAN No: {platform.panNumber}</div>}
              </td>
              <td style={{ border: 'none', padding: 0, verticalAlign: 'top', textAlign: 'right' }}>
                <p style={styles.title}>TAX INVOICE</p>
                <div style={{ marginTop: 8 }}>Invoice# {invoice.invoiceNumber}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 20 }}>
          <span><strong>Invoice Date :</strong> {formatDate(invoice.createdAt)}</span>
          <span><strong>Terms :</strong> {paymentTerms}</span>
          <span><strong>Due Date :</strong> {formatDate(invoice.dueDate)}</span>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={styles.bold}>Bill To</div>
          <div style={{ ...styles.bold, fontSize: 13, marginTop: 4 }}>{invoice.companyName}</div>
          {tenantAddressLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
          {tenantGst && <div style={{ marginTop: 4 }}>GSTIN {tenantGst}</div>}
          {placeOfSupply && <div>Place Of Supply: {placeOfSupply}</div>}
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '4%' }}>#</th>
              <th style={{ ...styles.th, width: '28%' }}>Item &amp; Description</th>
              <th style={{ ...styles.th, width: '12%' }}>HSN/SAC</th>
              <th style={{ ...styles.th, width: '8%', ...styles.right }}>Qty</th>
              <th style={{ ...styles.th, width: '14%', ...styles.right }}>Rate</th>
              <th style={{ ...styles.th, width: '16%', ...styles.right }}>IGST</th>
              <th style={{ ...styles.th, width: '18%', ...styles.right }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.cell}>1</td>
              <td style={styles.cell}>{itemName}</td>
              <td style={styles.cell}>{hsnSac}</td>
              <td style={{ ...styles.cell, ...styles.right }}>1.00</td>
              <td style={{ ...styles.cell, ...styles.right }}>{formatAmount(taxable)}</td>
              <td style={{ ...styles.cell, ...styles.right }}>
                {invoice.gstEnabled ? (
                  <>
                    {formatAmount(gstAmount)}
                    <br />
                    <span style={{ fontSize: 11, color: '#444' }}>{gstRate}%</span>
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td style={{ ...styles.cell, ...styles.right }}>{formatAmount(taxable)}</td>
            </tr>
            <tr>
              <td style={{ ...styles.cell, borderTop: 'none' }} colSpan={5} />
              <td style={{ ...styles.cell, ...styles.right }}>Sub Total</td>
              <td style={{ ...styles.cell, ...styles.right, ...styles.bold }}>{formatAmount(taxable)}</td>
            </tr>
            {invoice.gstEnabled && gstAmount > 0 && (
              <tr>
                <td style={{ ...styles.cell, borderTop: 'none' }} colSpan={5} />
                <td style={{ ...styles.cell, ...styles.right }}>{igstLabel}</td>
                <td style={{ ...styles.cell, ...styles.right, ...styles.bold }}>{formatAmount(gstAmount)}</td>
              </tr>
            )}
            <tr>
              <td style={{ ...styles.cell, borderTop: 'none' }} colSpan={5} />
              <td style={{ ...styles.cell, ...styles.right, ...styles.bold }}>Total</td>
              <td style={{ ...styles.cell, ...styles.right, ...styles.bold }}>{formatCurrency(total)}</td>
            </tr>
            <tr>
              <td style={{ ...styles.cell, borderTop: 'none' }} colSpan={5} />
              <td style={{ ...styles.cell, ...styles.right, ...styles.bold }}>Balance Due</td>
              <td style={{ ...styles.cell, ...styles.right, ...styles.bold }}>{formatCurrency(balanceDue)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <strong>Total In Words:</strong>
          <br />
          {indianAmountInWords(total)}
        </div>

        <table style={{ ...styles.table, border: 'none' }}>
          <tbody>
            <tr>
              <td style={{ border: 'none', padding: 0, verticalAlign: 'bottom', width: '65%' }}>
                <div style={styles.bold}>Notes</div>
                {bankNotes.map((line, i) => (
                  <div key={`${i}-${line}`}>{line}</div>
                ))}
              </td>
              <td style={{ border: 'none', padding: 0, verticalAlign: 'bottom', textAlign: 'right' }}>
                {platform?.signature && (
                  <img
                    src={platform.signature}
                    alt="Authorized signature"
                    style={{ maxHeight: 56, maxWidth: 140, objectFit: 'contain', marginBottom: 4 }}
                  />
                )}
                <div style={{ borderTop: '1px solid #333', display: 'inline-block', paddingTop: 4, fontWeight: 600 }}>
                  Authorized Signature
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Box>
  );
};

export const printPlatformInvoice = () => {
  const content = document.getElementById('platform-invoice-print');
  if (!content) return;

  const printStyles = `
    @page { size: A4; margin: 12mm; }
    body { margin: 0; font-family: "Helvetica Neue", Helvetica, Arial, sans-serif; font-size: 12px; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #333; padding: 6px 8px; font-size: 12px; vertical-align: top; }
    th { background: #f5f5f5; font-weight: 700; }
    img { max-height: 56px; object-fit: contain; }
  `;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) return;
  printWindow.document.write(`
    <html><head><title>Invoice</title><style>${printStyles}</style></head><body>${content.innerHTML}</body></html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 400);
};

export const downloadPlatformInvoicePdf = async (invoiceNumber: string) => {
  const { downloadElementAsPdf } = await import('../../utils/downloadPdf');
  const safeName = invoiceNumber.replace(/[^\w-]+/g, '_');
  await downloadElementAsPdf('platform-invoice-print', safeName);
};
